// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import type {Popout as PopoutDefinition, PopoutKey} from '@app/features/ui/popover';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('Popout');
interface FocusRestoreMeta {
	target: HTMLElement | null;
	keyboardModeEnabled: boolean;
}
class Popout {
	popouts: Record<string, PopoutDefinition> = {};
	popoutOrder: Array<string> = [];
	private focusReturnMeta = new Map<string, FocusRestoreMeta>();
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	open(popout: PopoutDefinition): void {
		logger.debug(`Opening popout: ${popout.key || 'unknown'}`);
		const key = this.normalizeKey(popout.key);
		const focusTarget = popout.returnFocusRef?.current ?? popout.target ?? null;
		this.focusReturnMeta.set(key, {
			target: focusTarget,
			keyboardModeEnabled: KeyboardMode.keyboardModeEnabled,
		});
		runInAction(() => {
			const normalizedDependsOn = popout.dependsOn != null ? this.normalizeKey(popout.dependsOn) : undefined;
			const popoutWithNormalizedDependency = normalizedDependsOn ? {...popout, dependsOn: normalizedDependsOn} : popout;
			if (!popout.dependsOn) {
				this.popouts = {[key]: popoutWithNormalizedDependency};
				this.popoutOrder = [key];
			} else {
				const parentChain = this.getParentPopoutChain(normalizedDependsOn!);
				const parentKeys = this.popoutOrder.filter((k) => k in parentChain && k !== key);
				this.popouts = {
					...parentChain,
					[key]: popoutWithNormalizedDependency,
				};
				this.popoutOrder = [...parentKeys, key];
			}
		});
		popout.onOpen?.();
	}
	close(key?: string | number): void {
		logger.debug(`Closing popout${key ? `: ${key}` : ''}`);
		if (key == null) {
			runInAction(() => {
				this.popouts = {};
				this.popoutOrder = [];
			});
			this.focusReturnMeta.clear();
			return;
		}
		let closingPopout: PopoutDefinition | undefined;
		let focusMeta: FocusRestoreMeta | null = null;
		const keyStr = this.normalizeKey(key);
		runInAction(() => {
			const targetPopout = this.popouts[keyStr];
			closingPopout = targetPopout;
			if (!targetPopout) return;
			focusMeta = this.focusReturnMeta.get(keyStr) ?? {
				target: targetPopout.returnFocusRef?.current ?? targetPopout.target ?? null,
				keyboardModeEnabled: KeyboardMode.keyboardModeEnabled,
			};
			const newPopouts = {...this.popouts};
			const parentChain = targetPopout.dependsOn
				? this.getParentPopoutChain(this.normalizeKey(targetPopout.dependsOn))
				: {};
			this.removePopoutAndDependents(keyStr, newPopouts);
			Object.assign(newPopouts, parentChain);
			this.popouts = newPopouts;
			this.popoutOrder = this.popoutOrder.filter((k) => k in newPopouts);
		});
		closingPopout?.onClose?.();
		this.focusReturnMeta.delete(keyStr);
		this.scheduleFocus(focusMeta);
	}
	closeAll(): void {
		logger.debug('Closing all popouts');
		const currentPopouts = Object.values(this.popouts);
		currentPopouts.forEach((popout) => {
			popout.onClose?.();
		});
		runInAction(() => {
			this.popouts = {};
			this.popoutOrder = [];
		});
		this.focusReturnMeta.clear();
	}
	reposition(key: PopoutKey): void {
		const normalizedKey = this.normalizeKey(key);
		const existingPopout = this.popouts[normalizedKey];
		if (!existingPopout) return;
		runInAction(() => {
			this.popouts = {
				...this.popouts,
				[normalizedKey]: {
					...existingPopout,
					shouldReposition: true,
				},
			};
		});
	}
	isOpen(key: PopoutKey): boolean {
		return this.normalizeKey(key) in this.popouts;
	}
	hasDependents(key: PopoutKey): boolean {
		const normalizedKey = this.normalizeKey(key);
		return Object.values(this.popouts).some((popout) =>
			popout.dependsOn ? this.normalizeKey(popout.dependsOn) === normalizedKey : false,
		);
	}
	getPopouts(): Array<PopoutDefinition> {
		return this.popoutOrder
			.map((key) => this.popouts[key])
			.filter((popout): popout is PopoutDefinition => popout != null);
	}
	private getParentPopoutChain(dependsOnKey: string): Record<string, PopoutDefinition> {
		const result: Record<string, PopoutDefinition> = {};
		let currentKey: string | undefined = dependsOnKey;
		while (currentKey != null) {
			const popout: PopoutDefinition = this.popouts[currentKey];
			if (!popout) break;
			result[currentKey] = popout;
			currentKey = popout.dependsOn ? this.normalizeKey(popout.dependsOn) : undefined;
		}
		return result;
	}
	private removePopoutAndDependents(key: string, popouts: Record<string, PopoutDefinition>): void {
		const dependentKeys = Object.entries(popouts)
			.filter(([_, popout]) => (popout.dependsOn ? this.normalizeKey(popout.dependsOn) === key : false))
			.map(([k]) => k);
		dependentKeys.forEach((depKey) => {
			this.removePopoutAndDependents(depKey, popouts);
			this.focusReturnMeta.delete(depKey);
		});
		delete popouts[key];
		this.focusReturnMeta.delete(key);
	}
	private scheduleFocus(meta: FocusRestoreMeta | null): void {
		const retries = 5;
		logger.debug(
			`Popout.scheduleFocus target=${meta?.target ? meta.target.tagName : 'null'} keyboardMode=${meta?.keyboardModeEnabled ?? false}`,
		);
		if (!meta || !meta.target) return;
		const {target, keyboardModeEnabled} = meta;
		queueMicrotask(() => {
			const hasHiddenAncestor = (element: HTMLElement): boolean =>
				Boolean(element.closest('[aria-hidden="true"], [data-floating-ui-inert]'));
			const attemptFocus = (remainingRetries: number): void => {
				if (!target.isConnected) {
					logger.debug('Popout.scheduleFocus aborted: target disconnected');
					return;
				}
				if (hasHiddenAncestor(target) && remainingRetries > 0) {
					requestAnimationFrame(() => attemptFocus(remainingRetries - 1));
					return;
				}
				try {
					target.focus({preventScroll: true});
					logger.debug('Popout.scheduleFocus applied focus to target');
				} catch (error) {
					logger.error('Popout.scheduleFocus failed to focus target', error as Error);
					return;
				}
				if (keyboardModeEnabled) {
					logger.debug('Popout.scheduleFocus re-entering keyboard mode');
					KeyboardMode.enterKeyboardMode(false);
				}
			};
			attemptFocus(retries);
		});
	}
	private normalizeKey(key: PopoutKey | string): string {
		return typeof key === 'string' ? key : key.toString();
	}
}
export default new Popout();
